import { computed, ref, readonly } from 'vue'
import { useViewManager } from '../view-manager'
import { WidgetViews, type Message } from '@/types'

import getAppName from '@/lib/widget-apps'
import useWebsocket from '@/api/websocket'
import { viewsKeys, widgetViews } from '@/lib/views'
import type { View } from '../view-manager/types'
import type { Track } from '../tracker'
import { useEventBus } from '@vueuse/core'
import { useI18n } from '../i18n'
import useWidgetOptions from '../widget-options'
import * as Sentry from '@sentry/vue'
import useEmbed from '@/embed/code'
import type { WorkflowExecutionPhase } from '@/embed/types'

const {
    changeView,
    currentView,
    currentViewOptions,
} = useViewManager(viewsKeys)

const lastInnerViewOfPreviousView = ref<View>(null)

const { waitForNewMessage, messages, reset: resetWebsocket } = useWebsocket()
const { options: widgetOptions } = useWidgetOptions()

const activeMessage = ref<Message | null>(null)

function useWidgetViewManager() {
    const initialize = () => {
        changeView(WidgetViews.LOADING, { hideBack: true })
    }

    const startWidgetManager = (value: Message) => {
        const { t } = useI18n()

        setViewMessage(value)

        if (value.workflowStatus === 'COMPLETED') {
            changeView('workflow-finish')
            return
        }

        if (value.workflowStatus === 'FAILED') {
            showError({
                title: t('views.widget.initialization_failed.title'),
                description: t('views.widget.initialization_failed.description'),
                retry: true,
                svgIcon: 'bug.svg',
            })
            return
        }

        // get app view based on
        const appName = getAppName(value.taskName || value.taskType, value.providerName)

        if (appName) {
            changeView(appName)
            return
        }

        changeView(WidgetViews.ERROR)
    }

    const setViewMessage = (value: Message) => {
        activeMessage.value = value

        Sentry.setTags({
            workspace_id: activeMessage.value.workspaceId,
            workflow_id: activeMessage.value.workflowId,
            task_name: activeMessage.value.taskName,
            task_type: activeMessage.value.taskType,
            task_id: activeMessage.value.taskId,
        })
    }

    const setLastInnerView = (value: View) => {
        lastInnerViewOfPreviousView.value = value
    }

    const getAppViewMessages = computed<Message[]>(() => {
        return messages.value.filter((message) => {
            return message.taskId === activeMessage.value?.taskId
        })
    })

    const finishAppView = async (options: {
        track?: Track
        loader?: boolean
    } = {
        loader: true,
    }) => {
        if (options.track) {
            options.track?.('screenView', 'verificationSuccess')
        }

        if (widgetOptions.value.loading && options.loader) {
            await changeView(WidgetViews.PROCESSING, { hideBack: true })
        }

        const messages = getAppViewMessages.value
        const lastMessage = messages[messages.length - 1]
        const newMessage = await waitForNewMessage(lastMessage.taskId)

        if (newMessage.workflowStatus) {
            const workflowPhaseDict: Record<Message['workflowStatus'], WorkflowExecutionPhase> = {
                CREATED: 'init',
                RUNNING: 'running',
                COMPLETED: 'completed',
                AWAITING: 'init',
                FAILED: 'failed',
                TIMEOUT: 'failed',
                TERMINATED: 'failed',
                PAUSED: 'failed',
            }

            useEmbed().setWorkflowExecutionPhase(
                workflowPhaseDict[newMessage.workflowStatus],
            )
        }

        const isWorkflowFinish = ['COMPLETED', 'AWAITING', 'FAILED'].includes(newMessage.workflowStatus)

        setViewMessage(newMessage)

        const appName = getAppName(newMessage.taskName || newMessage.taskType, newMessage.providerName)

        await changeView(
            isWorkflowFinish
                ? 'workflow-finish'
                : appName,
        )

        return true
    }

    const currentViewComponent = computed(() => widgetViews[currentView.value as keyof typeof widgetViews])

    const reset = () => {
        activeMessage.value = null
        changeView(WidgetViews.LOADING)
        resetWebsocket()
    }

    const goToPreviousView = () => {
        changeView(currentViewOptions.value.previousView)
    }

    type ShowErrorOptions = {
        title?: string
        description?: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error?: any
        retry?: boolean
        svgIcon?: string
    }

    const showError = (options: ShowErrorOptions = { retry: true }) => {
        const error = options.error?.response?.data || options.error

        useEmbed().setWorkflowExecutionPhase('failed')

        const errorMessage = Array.isArray(error?.errors)
            ? error?.errors.map((err: Record<string, string>) => err.message || err.code || err.outputMessage).filter(Boolean).join(', ')
            : error?.message || error?.code || error?.outputMessage || error

        changeView(WidgetViews.ERROR, {
            ...options,
            error: errorMessage,
            errorDetails: error,
        })
    }

    const showTimeoutError = (options?: Partial<ShowErrorOptions>) => {
        const { t } = useI18n()
        showError({
            title: t('views.widget.workflow_timeout.title'),
            description: t('views.widget.workflow_timeout.description'),
            svgIcon: 'timeout.svg',
            retry: false,
            ...options || {},
        })
    }

    const handleWorkflowTimeout = () => {
        useEventBus<Message>('websocket-message').on(message => {
            if (message.workflowStatus === 'TIMEOUT') {
                showTimeoutError()
            }
        })
    }

    return {
        initialize,
        changeView,
        currentView,
        currentViewComponent,
        currentViewOptions,
        lastInnerViewOfPreviousView: readonly(lastInnerViewOfPreviousView),
        setLastInnerView,
        startWidgetManager,
        finishAppView,
        getAppViewMessages,
        activeMessage: readonly(activeMessage),
        reset,
        goToPreviousView,
        showError,
        showTimeoutError,
        handleWorkflowTimeout,
    }
}

export default useWidgetViewManager
